// pages/AboutPage.jsx
import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Button,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Storefront as StorefrontIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Dashboard as DashboardIcon,
  SupportAgent as SupportAgentIcon,
  Stars as StarsIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

const AboutPage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ar';

  const goldAccent = '#E6B800';
  const goldBright = '#FFD700';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const cardBg = 'rgba(255, 255, 255, 0.025)';
  const border = 'rgba(230, 184, 0, 0.15)';

  // SEO content
  const seoTitle = isRtl
    ? 'من نحن | QR Table – منصة متاجر ذكية'
    : 'About Us | QR Table – Smart Store Platform';
  const seoDescription = isRtl
    ? 'تعرف على منصة QR Table المتكاملة لإدارة المتاجر: قوائم QR، نظام نقاط بيع، تحليلات مبيعات، ودعم فني 24/7.'
    : 'Discover QR Table — an integrated store platform with QR menus, POS, sales analytics, and 24/7 support.';
  const seoKeywords = isRtl
    ? 'QR Table, منصة متاجر, نظام نقاط بيع, قائمة QR, إدارة مطاعم, ماليزيا'
    : 'QR Table, store platform, POS system, QR menu, restaurant management, Malaysia';
  const canonicalUrl =
    typeof window !== 'undefined' ? window.location.href : 'https://qrtable.app/about';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const phoneNumbers = ['+60 18-324 8864', '+60 11-2833 4847'];
  const address = isRtl
    ? '112، طريق بوكيت بينتانج، بوكيت بينتانج، 55100 كوالالمبور، ماليزيا'
    : '112, Bukit Bintang Rd, Bukit Bintang, 55100 Kuala Lumpur, Malaysia';
  const mapsEmbed =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.802943798399!2d101.7108202!3d3.14664!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc372769d986d7%3A0xcc3e4f755b23565c!2sRestoran%20Shawarma%20%2F%20Reto%20Sandwich!5e0!3m2!1sen!2seg!4v1771180959044!5m2!1sen!2seg';
  const mapsLink =
    'https://www.google.com/maps/place/Restoran+Shawarma+%2F+Reto+Sandwich/@3.14664,101.7108202,17z';

  const platformFeatures = isRtl
    ? [
        { emoji: '📱', text: 'قائمة متجر ورمز QR مخصص' },
        { emoji: '🔔', text: 'إشعارات فورية للطلبات' },
        { emoji: '📊', text: 'تحليل المبيعات والإيرادات' },
        { emoji: '🚚', text: 'إدارة التوصيل ومدى الخدمة' },
        { emoji: '💳', text: 'دعم متعدد العملات' },
      ]
    : [
        { emoji: '📱', text: 'Store Menu & Custom QR Code' },
        { emoji: '🔔', text: 'Instant Order Notifications' },
        { emoji: '📊', text: 'Sales & Revenue Analytics' },
        { emoji: '🚚', text: 'Delivery Management & Service Range' },
        { emoji: '💳', text: 'Multi-Currency Support' },
      ];

  const values = isRtl
    ? [
        { emoji: '🌟', text: 'الابتكار والتطوير المستمر' },
        { emoji: '💛', text: 'الشفافية والثقة مع عملائنا' },
        { emoji: '✨', text: 'الجودة والاحترافية في الخدمة' },
        { emoji: '🔄', text: 'دعم فني على مدار الساعة' },
        { emoji: '😊', text: 'رضا العملاء أولويتنا' },
      ]
    : [
        { emoji: '🌟', text: 'Continuous Innovation & Development' },
        { emoji: '💛', text: 'Transparency & Trust with Our Clients' },
        { emoji: '✨', text: 'Quality & Professionalism in Service' },
        { emoji: '🔄', text: '24/7 Technical Support' },
        { emoji: '😊', text: 'Customer Satisfaction is Our Priority' },
      ];

  // Shared card sx
  const glassCardSx = {
    height: '100%',
    bgcolor: cardBg,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid rgba(255,255,255,0.06)`,
    borderRadius: { xs: '18px', sm: '22px' },
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 2,
      background: `linear-gradient(90deg, transparent, ${goldBright}80, transparent)`,
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      borderColor: border,
      boxShadow: '0 16px 40px rgba(230, 184, 0, 0.15)',
      '&::before': { opacity: 1 },
    },
  };

  const iconBadgeSx = {
    width: { xs: 60, sm: 72, md: 80 },
    height: { xs: 60, sm: 72, md: 80 },
    borderRadius: '50%',
    background: softGold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mb: 2,
    border: `2px solid ${border}`,
    '& .MuiSvgIcon-root': {
      fontSize: { xs: 30, sm: 36, md: 42 },
      color: goldBright,
    },
  };

  const sectionTitleSx = {
    fontWeight: 800,
    color: goldBright,
    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
    letterSpacing: -0.3,
    mb: 2,
  };

  const bodySx = {
    fontSize: { xs: '0.92rem', sm: '1rem' },
    lineHeight: 1.8,
    color: '#D0D0D0',
  };

  // JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'QR Table',
    url: 'https://qrtable.app',
    logo: 'https://qrtable.app/logo.svg',
    description: seoDescription,
    telephone: phoneNumbers,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '112, Bukit Bintang Rd',
      addressLocality: 'Bukit Bintang',
      postalCode: '55100',
      addressRegion: 'Kuala Lumpur',
      addressCountry: 'MY',
    },
    sameAs: [],
  };

  return (
    <>
      {/* ==================== SEO HEAD ==================== */}
      <Helmet htmlAttributes={{ lang: isRtl ? 'ar' : 'en', dir: isRtl ? 'rtl' : 'ltr' }}>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="QR Table" />
        <meta property="og:locale" content={isRtl ? 'ar_AR' : 'en_US'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />

        {/* Structured data */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ==================== PAGE ==================== */}
      <Box
        component="main"
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          bgcolor: '#0A0A0A',
          minHeight: '100vh',
          color: '#FFFFFF',
          pb: { xs: 12, md: 14 },
          background: `
            radial-gradient(circle at 10% 0%, rgba(230,184,0,0.06) 0%, transparent 45%),
            radial-gradient(circle at 90% 100%, rgba(230,184,0,0.04) 0%, transparent 45%),
            #0A0A0A
          `,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 5, sm: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}
        >
          {/* ==================== HEADER ==================== */}
          <Box
            component="header"
            sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 6 } }}
          >
            {/* Logo */}
            <Box
              onClick={() => navigate('/')}
              role="link"
              aria-label={isRtl ? 'العودة للرئيسية' : 'Back to home'}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                height: { xs: 60, sm: 70, md: 80 },
                width: { xs: 60, sm: 70, md: 80 },
                mx: 'auto',
                transition: 'transform 0.25s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 120 120"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="QR Table logo"
              >
                <defs>
                  <linearGradient id="aboutGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFD700" />
                    <stop offset="100%" stopColor="#E6A800" />
                  </linearGradient>
                  <linearGradient id="aboutDarkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1F1F1F" />
                    <stop offset="100%" stopColor="#0D0D0D" />
                  </linearGradient>
                  <filter id="aboutShadow">
                    <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000" floodOpacity="0.5" />
                  </filter>
                </defs>

                <circle cx="60" cy="60" r="55" fill="url(#aboutDarkGrad)" stroke="url(#aboutGoldGrad)" strokeWidth="3" />
                <circle cx="60" cy="60" r="50" fill="none" stroke="#FFD700" strokeWidth="0.8" strokeDasharray="3 5" opacity="0.2" />

                <ellipse cx="60" cy="62" rx="32" ry="8" fill="url(#aboutGoldGrad)" stroke="#FFD700" strokeWidth="1.5" filter="url(#aboutShadow)" />
                <rect x="56" y="68" width="8" height="16" fill="url(#aboutGoldGrad)" rx="2" />
                <ellipse cx="60" cy="86" rx="20" ry="5" fill="url(#aboutGoldGrad)" stroke="#FFD700" strokeWidth="1" />
                <ellipse cx="60" cy="86" rx="16" ry="3.5" fill="#0D0D0D" />

                <g transform="translate(60, 52) rotate(-8)">
                  <rect x="-18" y="-20" width="36" height="36" rx="3" fill="#FFD700" filter="url(#aboutShadow)" />
                  <rect x="-15" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="-13" y="-15" width="3" height="3" fill="#FFD700" />
                  <rect x="8" y="-17" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="10" y="-15" width="3" height="3" fill="#FFD700" />
                  <rect x="-15" y="8" width="7" height="7" fill="#0D0D0D" rx="1" />
                  <rect x="-13" y="10" width="3" height="3" fill="#FFD700" />
                  <rect x="-6" y="-17" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-1" y="-14" width="3" height="3" fill="#0D0D0D" />
                  <rect x="4" y="-17" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-6" y="-11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="0" y="-8" width="3" height="3" fill="#0D0D0D" />
                  <rect x="5" y="-11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-15" y="-6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="-3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="-2" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="-4" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="-6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="-3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-15" y="1" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="4" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="2" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="5" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="3" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="6" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-10" y="9" width="3" height="3" fill="#0D0D0D" />
                  <rect x="-4" y="11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="1" y="9" width="3" height="3" fill="#0D0D0D" />
                  <rect x="7" y="11" width="3" height="3" fill="#0D0D0D" />
                  <rect x="12" y="9" width="3" height="3" fill="#0D0D0D" />
                </g>

                <text x="42" y="108" textAnchor="middle" fontSize="22" fontWeight="900" fill="#FFD700" fontFamily="Arial, Helvetica, sans-serif">QR</text>
                <text x="78" y="108" textAnchor="middle" fontSize="20" fontWeight="400" fill="#FFFFFF" fontFamily="Arial, Helvetica, sans-serif">table</text>
                <line x1="30" y1="114" x2="90" y2="114" stroke="#FFD700" strokeWidth="1" opacity="0.5" />
                <circle cx="60" cy="114" r="1.5" fill="#FFD700" />
              </svg>
            </Box>

            <Typography
              component="h1"
              variant="h1"
              sx={{
                mt: 1.5,
                fontWeight: 900,
                background: 'linear-gradient(135deg, #FFFFFF, #FFD700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: { xs: 1, sm: 2, md: 3 },
                fontSize: { xs: '1.6rem', sm: '2rem', md: '2.4rem' },
                mb: 1,
              }}
            >
              QR Table
            </Typography>

            <Typography
              component="p"
              sx={{
                color: '#A8A8A8',
                fontWeight: 400,
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                maxWidth: 680,
                mx: 'auto',
                px: 2,
                lineHeight: 1.7,
              }}
            >
              {isRtl
                ? 'منصة متكاملة لإدارة متجرك الإلكتروني بكل احترافية'
                : 'An integrated platform to manage your online store professionally'}
            </Typography>
          </Box>

          {/* ==================== CONTENT GRID ==================== */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* About / History */}
            <Grid item xs={12} md={6}>
              <Card component="article" sx={glassCardSx}>
                <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <Box sx={iconBadgeSx}>
                    <StorefrontIcon />
                  </Box>
                  <Typography component="h2" sx={sectionTitleSx}>
                    {isRtl ? 'منصة متكاملة للمتاجر' : 'Integrated Store Platform'}
                  </Typography>
                  <Typography component="p" sx={{ ...bodySx, mb: 2 }}>
                    {isRtl
                      ? 'منذ إطلاق منصة QR Table نسعى لتوفير أفضل الحلول التقنية لأصحاب المتاجر. نقدم منصة متكاملة تساعدك على إدارة متجرك بكل سهولة، من إنشاء قائمة المنتجات إلى تحليل المبيعات وإدارة الطلبات.'
                      : 'Since the launch of QR Table platform, we strive to provide the best technical solutions for store owners. We offer an integrated platform that helps you manage your store with ease — from creating product menus to sales analysis and order management.'}
                  </Typography>
                  <Typography component="p" sx={{ ...bodySx, color: '#9A9A9A' }}>
                    {isRtl
                      ? 'نحن نؤمن بأن التكنولوجيا يجب أن تكون في خدمة التجارة، لذلك قمنا بتصميم منصة سهلة الاستخدام تمكنك من التركيز على نمو عملك.'
                      : 'We believe that technology should serve commerce, which is why we designed an easy-to-use platform that allows you to focus on growing your business.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={6}>
              <Card component="article" sx={glassCardSx}>
                <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <Box sx={iconBadgeSx}>
                    <SupportAgentIcon />
                  </Box>
                  <Typography component="h2" sx={sectionTitleSx}>
                    {isRtl ? 'تواصل معنا' : 'Contact Us'}
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ my: 3 }}
                  >
                    {phoneNumbers.map((phone) => (
                      <Chip
                        key={phone}
                        component="a"
                        href={`tel:${phone.replace(/\s|-/g, '')}`}
                        icon={<PhoneIcon sx={{ color: `${goldBright} !important`, fontSize: '1rem !important' }} />}
                        label={phone}
                        clickable
                        aria-label={`${isRtl ? 'اتصل بنا على' : 'Call us at'} ${phone}`}
                        sx={{
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          height: 44,
                          px: 1,
                          direction: 'ltr',
                          bgcolor: 'rgba(255,255,255,0.03)',
                          color: '#FFFFFF',
                          border: `1px solid rgba(255,255,255,0.08)`,
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: border,
                            bgcolor: softGold,
                            color: goldBright,
                            transform: 'translateY(-2px)',
                          },
                        }}
                      />
                    ))}
                  </Stack>

                  <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      color: goldBright,
                      fontSize: { xs: '1.05rem', sm: '1.15rem' },
                      mb: 1.5,
                    }}
                  >
                    {isRtl ? 'أوقات الدعم الفني' : 'Support Hours'}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{
                      p: 1.75,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      borderRadius: '999px',
                      border: `1px solid rgba(255,255,255,0.06)`,
                      maxWidth: 420,
                      mx: 'auto',
                    }}
                  >
                    <AccessTimeIcon sx={{ color: goldBright, fontSize: 20 }} />
                    <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 600 }}>
                      {isRtl ? 'يوميًا من 9 صباحًا حتى 12 صباحًا' : 'Daily from 9 AM to 12 AM'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Card component="article" sx={glassCardSx}>
                <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                  <Box sx={iconBadgeSx}>
                    <LocationIcon />
                  </Box>
                  <Typography component="h2" sx={sectionTitleSx}>
                    {isRtl ? 'موقعنا' : 'Our Location'}
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.03)',
                      p: { xs: 2, sm: 3 },
                      borderRadius: '16px',
                      border: `1px solid rgba(255,255,255,0.06)`,
                      mb: 3,
                      maxWidth: 720,
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      component="h3"
                      sx={{ fontWeight: 700, color: goldBright, mb: 1, fontSize: '1rem' }}
                    >
                      {isRtl ? 'المقر الرئيسي' : 'Headquarters'}
                    </Typography>
                    <Typography
                      component="address"
                      sx={{
                        fontStyle: 'normal',
                        fontSize: { xs: '0.95rem', sm: '1.05rem' },
                        color: '#FFFFFF',
                        fontWeight: 600,
                        lineHeight: 1.6,
                      }}
                    >
                      {address}
                    </Typography>
                  </Box>

                  {/* Map */}
                  <Box
                    sx={{
                      mt: 2,
                      height: { xs: 280, sm: 350, md: 420 },
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: `1px solid rgba(255,255,255,0.06)`,
                    }}
                  >
                    <iframe
                      src={mapsEmbed}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={isRtl ? 'موقع QR Table على الخريطة' : 'QR Table Location on Map'}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                    onClick={() => window.open(mapsLink, '_blank', 'noopener,noreferrer')}
                    sx={{
                      mt: 3,
                      background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                      color: '#111',
                      fontWeight: 800,
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1.2, sm: 1.4 },
                      borderRadius: '999px',
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(230, 184, 0, 0.28)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${goldBright}, ${goldAccent})`,
                        boxShadow: '0 12px 32px rgba(230, 184, 0, 0.45)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {isRtl ? 'افتح الخريطة في Google Maps' : 'Open in Google Maps'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Features */}
            <Grid item xs={12} md={6}>
              <Card component="article" sx={glassCardSx}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={iconBadgeSx}>
                    <DashboardIcon />
                  </Box>
                  <Typography component="h2" sx={{ ...sectionTitleSx, textAlign: 'center' }}>
                    {isRtl ? 'ميزات المنصة' : 'Platform Features'}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {platformFeatures.map((feature, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          borderRadius: '12px',
                          border: `1px solid rgba(255,255,255,0.05)`,
                          transition: 'all 0.2s ease',
                          flexDirection: isRtl ? 'row-reverse' : 'row',
                          '&:hover': {
                            borderColor: border,
                            bgcolor: softGold,
                            transform: isRtl ? 'translateX(-4px)' : 'translateX(4px)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{feature.emoji}</Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.92rem', sm: '1rem' },
                            color: '#FFFFFF',
                            fontWeight: 600,
                            textAlign: isRtl ? 'right' : 'left',
                          }}
                        >
                          {feature.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Values */}
            <Grid item xs={12} md={6}>
              <Card component="article" sx={glassCardSx}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={iconBadgeSx}>
                    <StarsIcon />
                  </Box>
                  <Typography component="h2" sx={{ ...sectionTitleSx, textAlign: 'center' }}>
                    {isRtl ? 'قيمنا' : 'Our Values'}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {values.map((value, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          borderRadius: '12px',
                          border: `1px solid rgba(255,255,255,0.05)`,
                          transition: 'all 0.2s ease',
                          flexDirection: isRtl ? 'row-reverse' : 'row',
                          '&:hover': {
                            borderColor: border,
                            bgcolor: softGold,
                            transform: isRtl ? 'translateX(-4px)' : 'translateX(4px)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: '1.2rem', lineHeight: 1 }}>{value.emoji}</Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.92rem', sm: '1rem' },
                            color: '#FFFFFF',
                            fontWeight: 600,
                            textAlign: isRtl ? 'right' : 'left',
                          }}
                        >
                          {value.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* ==================== CTA BANNER ==================== */}
            <Grid item xs={12}>
              <Card
                component="section"
                aria-label={isRtl ? 'انضم إلينا اليوم' : 'Join us today'}
                sx={{
                  position: 'relative',
                  borderRadius: { xs: '18px', sm: '22px' },
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${goldBright} 0%, ${goldAccent} 100%)`,
                  boxShadow: '0 20px 50px rgba(230, 184, 0, 0.3)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    [isRtl ? 'left' : 'right']: '-20%',
                    width: '60%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.25), transparent 60%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 4, sm: 5, md: 6 },
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <StorefrontIcon sx={{ fontSize: 38, color: '#111' }} />
                  </Box>
                  <Typography
                    component="h2"
                    sx={{
                      fontWeight: 900,
                      color: '#111',
                      fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
                      letterSpacing: { xs: 0.5, md: 1.5 },
                      mb: 1.5,
                    }}
                  >
                    {isRtl ? 'انضم إلى QR Table اليوم!' : 'Join QR Table Today!'}
                  </Typography>
                  <Typography
                    component="p"
                    sx={{
                      color: 'rgba(0,0,0,0.75)',
                      fontWeight: 600,
                      fontSize: { xs: '0.95rem', sm: '1.1rem' },
                      maxWidth: 620,
                      mx: 'auto',
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    {isRtl
                      ? 'ابدأ رحلتك في التجارة الإلكترونية مع منصة متكاملة وسهلة الاستخدام'
                      : 'Start your e-commerce journey with an integrated and easy-to-use platform'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                    onClick={() => navigate('/register')}
                    sx={{
                      bgcolor: '#111',
                      color: 'rgb(189, 97, 58)',
                      fontWeight: 800,
                      px: { xs: 3, sm: 5 },
                      py: { xs: 1.3, sm: 1.6 },
                      borderRadius: '999px',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        bgcolor: '#1A1A1A',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
                      },
                    }}
                  >
                    {isRtl ? 'سجل الآن - مجانًا لمدة 15 يومًا' : 'Sign Up - 15 Days Free'}
                  </Button>
                  <Typography
                    sx={{
                      color: 'rgb(177, 125, 2)',
                      fontWeight: 500,
                      mt: 2.5,
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    }}
                  >
                    {isRtl
                      ? 'QR Table - منصة متاجر ذكية لأعمالك الناجحة'
                      : 'QR Table — Smart Store Platform for Your Successful Business'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default AboutPage;